"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Zap, Snowflake, Leaf, ArrowRight, Gauge, Droplets, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const benefitsList = [{
  id: 1,
  title: "Poupança energética comprovada",
  category: "Eficiência de Recursos",
  description: "Reduza o consumo de ar comprimido e a potência de bombagem com bicos de precisão calibrados para trabalhar à pressão exata.",
  metricValue: "-35%",
  metricLabel: "Consumo de ar comprimido",
  iconName: "zap"
}, {
  id: 2,
  title: "Desempenho térmico e arrefecimento",
  category: "Controlo Térmico",
  description: "Obtenha uma distribuição uniforme do tamanho de gota para uma taxa de evaporação acelerada e arrefecimento de gases e estruturas.",
  metricValue: "99.2%",
  metricLabel: "Uniformidade de cobertura",
  iconName: "snowflake"
}, {
  id: 3,
  title: "Impacto ambiental e sustentabilidade",
  category: "Pegada Ecológica",
  description: "Diminua o desperdício de água e produtos químicos em processos de lavagem e tratamento através de dosagem e atomização precisas.",
  metricValue: "-40%",
  metricLabel: "Desperdício hídrico e químico",
  iconName: "leaf"
}];
const technicalSpecs = [{
  id: 1,
  title: "Calibração micrométrica de gotas",
  detail: "Controlo rigoroso do D32 (diâmetro médio Sauter) para máxima eficiência de transferência."
}, {
  id: 2,
  title: "Menor perda de carga hidráulica",
  detail: "Perfis internos desenvolvidos para evitar turbulência desnecessária e desgaste prematuro."
}, {
  id: 3,
  title: "Manutenção e ciclo de vida alargado",
  detail: "Materiais resistentes a corrosão e abrasão que mantêm o padrão de leque por mais horas."
}];
export default function Benefits() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "zap":
        return <Zap className="size-6 text-primary" />;
      case "snowflake":
        return <Snowflake className="size-6 text-secondary" />;
      case "leaf":
        return <Leaf className="size-6 text-chart-3" />;
      default:
        return <Gauge className="size-6 text-primary" />;
    }
  };
  return <section id="technicalbenefits" data-nav="light" className="relative bg-background py-20 lg:py-28 overflow-hidden">
      {/* Decorative background grid subtle overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(hsl(var(--color-primary)/0.05)_1px,transparent_1px)] [background-size:24px_24px] pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <Badge variant="outline" className="mb-4 border-primary/20 bg-accent text-primary px-3 py-1 font-mono text-xs">
            Engenharia de Pulverização Industrial
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground tracking-tight mb-4">
            Vantagens técnicas mensuráveis para a sua operação
          </h2>
          <p className="font-default text-base sm:text-lg text-muted-foreground leading-relaxed">
            A tecnologia Spraying Systems Co. combina precisão hidrodinâmica com poupança energética e redução direta do impacto ambiental.
          </p>
        </div>

        {/* 3 Main Technical Benefits Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {benefitsList.map((item, i) => <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 24
        }} whileInView={shouldReduceMotion ? false : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          delay: i * 0.12
        }} className="flex">
              <Card className="flex flex-col justify-between w-full border border-border/80 shadow-md hover:shadow-lg transition-[border-color,box-shadow] duration-300 hover:border-primary/40 bg-card rounded-xl">
                <CardContent className="p-6 sm:p-8 flex flex-col h-full">
                  <div className="flex items-center justify-between mb-6">
                    <div className="size-12 rounded-lg bg-muted flex items-center justify-center">
                      {getIcon(item.iconName)}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground uppercase tracking-wide">
                      {item.category}
                    </span>
                  </div>

                  <h3 className="font-default text-xl font-bold text-card-foreground mb-3">
                    {item.title}
                  </h3>

                  <p className="font-default text-sm text-muted-foreground leading-relaxed mb-6 grow">
                    {item.description}
                  </p>

                  <Separator className="mb-6" />

                  <div className="bg-muted/50 rounded-lg p-4 flex items-baseline justify-between border border-border/40">
                    <div>
                      <span className="block text-2xl lg:text-3xl font-bold text-primary font-mono tracking-tight">
                        {item.metricValue}
                      </span>
                      <span className="text-xs text-muted-foreground font-medium">
                        {item.metricLabel}
                      </span>
                    </div>
                    <Gauge className="size-5 text-muted-foreground/60 self-center" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>

        {/* Deep Dive Engineering Banner */}
        <motion.div initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 20
      }} whileInView={shouldReduceMotion ? false : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.6
      }} className="rounded-2xl bg-primary text-primary-foreground p-8 lg:p-12 shadow-xl border border-primary/20 relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
            <div className="lg:col-span-6 space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-white/10 text-primary-foreground text-xs font-mono">
                <Droplets className="size-3.5" />
                <span>Auditoria e Otimização de Sistemas</span>
              </div>
              <h3 className="font-default text-2xl sm:text-3xl font-bold tracking-tight text-primary-foreground">
                Otimize os parâmetros do seu processo industrial
              </h3>
              <p className="font-default text-sm sm:text-base text-primary-foreground/80 leading-relaxed">
                Os nossos engenheiros analisam a pressão de trabalho, vazão, ângulo de cobertura e materiais para garantir a máxima eficiência técnica com retorno de investimento garantido.
              </p>
              <div className="pt-2 flex flex-wrap gap-4">
                <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90 shadow-sm font-medium">
                  <Link to="/TechnicalSolutionsPage">
                    Ver soluções técnicas
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-white/30 text-primary-foreground hover:bg-white/10 bg-transparent">
                  <Link to="/ContactsPage">
                    Consulte os nossos engenheiros
                  </Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-6 grid grid-cols-1 gap-3.5">
              {technicalSpecs.map((spec, i) => <div key={spec.id} data-index={i} className="rounded-lg bg-white/5 border border-white/10 p-4 transition-colors hover:bg-white/10">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="size-5 text-secondary-foreground shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-default text-sm font-semibold text-primary-foreground mb-1">
                        {spec.title}
                      </h4>
                      <p className="font-default text-xs text-primary-foreground/75 leading-relaxed">
                        {spec.detail}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}